import classNames from 'classnames';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Box,
  Card,
  Heading,
  CardHero,
  AutoGrid,
  CardFooter,
  CardHeader,
  Typography,
  CardContent,
  AllStandardColourOptions,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Services
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';
import { BrandLogo } from '@/components/Graphics/BrandLogo';

type WithBrandFeaturesProps = {
  sliceId: string;
  slice: Content.RegionSliceWithBrandFeatures;
};

export const WithBrandFeatures = ({
  slice,
  sliceId,
}: WithBrandFeaturesProps) => {
  const { primary } = slice;
  const { title, caption, features } = primary;

  const { WebsiteUrl } = applicationEnv();

  return (
    <>
      <Box component="div" className="headline align-center">
        <Box component="div" addFlow>
          <Heading component="h2" addHeadlineTitle>
            {asText(title)}
          </Heading>

          {isFilled.richText(caption) && (
            <Typography component="p" className="headline__sub">
              {asText(caption)}
            </Typography>
          )}
        </Box>
      </Box>

      <AutoGrid component="div" placement="auto-fit">
        {(features ?? []).map((feature, index) => {
          const { colour_scheme } = feature;

          let colour: AllStandardColourOptions | undefined = undefined;

          if (isFilled.select(colour_scheme)) {
            colour = {
              SHL: 'sky' satisfies AllStandardColourOptions,
              'SH.UK': 'blue' satisfies AllStandardColourOptions,
              Preventx: 'indigo' satisfies AllStandardColourOptions,
              'Test.me': 'purple' satisfies AllStandardColourOptions,
              'Test For Me': 'pink' satisfies AllStandardColourOptions,
              'FreeTest.me': 'orange' satisfies AllStandardColourOptions,
              'Soapoli Online': 'pink' satisfies AllStandardColourOptions,
            }[colour_scheme] as AllStandardColourOptions;
          }

          return (
            <Card
              colour={colour}
              component="article"
              key={`region-brand-features-${sliceId}-${index + 1}`}
            >
              {isFilled.select(feature.brand_image) && (
                <CardHero component="div">
                  <BrandLogo logo={feature.brand_image} />
                </CardHero>
              )}

              <CardHeader addSROnly={feature.is_title_hidden}>
                <Heading component="h3">{asText(feature.title)}</Heading>
              </CardHeader>

              <CardContent addFlow>
                <RichText
                  variant="default"
                  websiteUrl={WebsiteUrl}
                  content={feature.content}
                />
              </CardContent>

              {isFilled.link(feature.link) && (
                <CardFooter>
                  <Link
                    as="default"
                    type="button"
                    variant="ghost"
                    retainSearchParams
                    href={feature.link}
                    className="card__action"
                  >
                    {feature.link.text}
                  </Link>
                </CardFooter>
              )}
            </Card>
          );
        })}
      </AutoGrid>
    </>
  );
};
